import type { ReactElement } from 'react'
import { RefreshCw } from 'lucide-react'
import { RolesInput } from '../RolesInput'

const INPUT_CLASSES =
  'w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500'
const LABEL_CLASSES = 'block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1'

const DEVICE_TYPES: Array<{ value: string; label: string }> = [
  { value: 'workstation', label: 'Workstation' },
  { value: 'server', label: 'Server' },
  { value: 'domain_controller', label: 'Domain Controller' },
  { value: 'file_server', label: 'File Server' },
  { value: 'mail_server', label: 'Mail Server' },
  { value: 'web_server', label: 'Web Server' },
  { value: 'database_server', label: 'Database Server' },
  { value: 'network_device', label: 'Network Device' },
  { value: 'mobile_device', label: 'Mobile Device' },
]

interface ErrorBannerProps {
  message: string
}

export function ErrorBanner({ message }: ErrorBannerProps): ReactElement {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-400">{message}</p>
    </div>
  )
}

interface HostnameFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
}

export function HostnameField({ value, onChange, placeholder }: HostnameFieldProps): ReactElement {
  return (
    <div>
      <label className={LABEL_CLASSES}>Hostname *</label>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className={INPUT_CLASSES}
        placeholder={placeholder}
        required
      />
    </div>
  )
}

interface DeviceTypeSelectProps {
  value: string
  onChange: (value: string) => void
}

export function DeviceTypeSelect({ value, onChange }: DeviceTypeSelectProps): ReactElement {
  return (
    <div>
      <label className={LABEL_CLASSES}>Device Type *</label>
      <select
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      >
        {DEVICE_TYPES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

interface IpFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
}

export function IpField({ value, onChange, placeholder }: IpFieldProps): ReactElement {
  return (
    <div>
      <label className={LABEL_CLASSES}>IP Address</label>
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className={INPUT_CLASSES}
        placeholder={placeholder}
      />
    </div>
  )
}

interface RolesFieldProps {
  value: string[]
  onChange: (roles: string[]) => void
  existingCustomRoles: string[]
}

export function RolesField({
  value,
  onChange,
  existingCustomRoles,
}: RolesFieldProps): ReactElement {
  return (
    <div>
      <label className={LABEL_CLASSES}>Roles</label>
      <RolesInput value={value} onChange={onChange} existingCustomRoles={existingCustomRoles} />
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Functional roles for this IT system (e.g., AD, DNS, DHCP)
      </p>
    </div>
  )
}

interface FormFooterProps {
  onClose: () => void
  creating: boolean
  canSubmit: boolean
}

export function FormFooter({ onClose, creating, canSubmit }: FormFooterProps): ReactElement {
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={creating || !canSubmit}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {creating && <RefreshCw className="h-4 w-4 animate-spin" />}
        Add Endpoint
      </button>
    </div>
  )
}

